/** VistoeraTASK-00441 — Image Editor "Save Edited Image As New" root-cause
 * fix: `exportEditedImageBlob` (`DualPaneCutoutEditor.tsx`) previously
 * ALWAYS encoded its full-resolution composited export as lossless PNG
 * regardless of whether the result actually contains any transparency.
 * The `destination-in` mask-cut is applied unconditionally in that
 * function, but the mask canvas defaults to fully-opaque white (see
 * `resetMask`/`initCanvasesTo` in `DualPaneCutoutEditor.tsx`) — so the
 * overwhelmingly common "paint a brush stroke, no actual background
 * cutout" edit still produces a fully-opaque result that was being
 * wastefully re-encoded as PNG.
 *
 * Real measurement (TASK-00441 forensics — Pillow re-encode of this
 * repo's own real, camera-realistic ~7MB/21MB/31MB JPEG test fixtures,
 * `_task00410_real_6mb_test.jpg` / `_task00410_test_20mb.jpg` /
 * `_task00410_test_30mb.jpg`, at native resolution, decode→save('PNG')):
 * PNG re-encoding a fully-opaque photographic JPEG inflates its size
 * ~5.5x — a legal ~31MB input becomes a ~173MB export. This is the
 * dominant, directly-measured driver of both "大图保存变慢" (far more
 * bytes to encode/upload) and "大图保存失败" (a ~173MB export routinely
 * exceeds typical Cloudflare proxy upload limits in front of
 * api-preview.vistoera.com — confirmed live via this task's own probe,
 * `Server: cloudflare` + `CF-RAY` present on that origin — and forces the
 * backend's own thumbnail-generation decode step down a path with no PNG
 * equivalent of Pillow's JPEG/WEBP "draft" subsampled decode, i.e. a full
 * undraftable full-resolution decode on the same synchronous upload
 * request TASK-00410 already found OOM-crashing the single Preview
 * Render instance for an analogous reason — see
 * `backend/app/saas/assets/renditions.py`'s `_open_rgb`).
 *
 * Fix: inspect the ACTUAL composited pixel data's alpha channel right
 * before encoding. Any real transparency (an actual cutout/erase
 * operation was used) still gets lossless PNG — correctness/quality is
 * never sacrificed, and this task's own spec explicitly forbids silently
 * dropping real transparency or defaulting to a low-quality JPEG. A
 * fully-opaque result (the common case) is encoded as a high-quality
 * JPEG instead, eliminating the inflation at its source.
 *
 * Pure, synchronously-testable logic deliberately extracted into its own
 * module (no Canvas/DOM APIs) so it has real unit test coverage without a
 * jsdom/canvas-mocking harness — `DualPaneCutoutEditor.tsx` is the sole
 * caller (`exportEditedImageBlob`). */

/** Scans an RGBA pixel buffer (as produced by `CanvasRenderingContext2D.
 * getImageData(...).data`) for any pixel whose alpha channel is not fully
 * opaque (255). Returns `true` the instant one is found — never scans the
 * remainder once the answer is already known (early-exit, matters for
 * large full-resolution buffers). An empty/degenerate buffer (length < 4)
 * is conservatively treated as "no transparency" (nothing to composite). */
export function imageDataHasTransparency(rgba: Uint8ClampedArray | Uint8Array): boolean {
  for (let i = 3; i < rgba.length; i += 4) {
    if (rgba[i] !== 255) return true
  }
  return false
}

export interface EditedImageExportFormat {
  mimeType: 'image/png' | 'image/jpeg'
  /** `undefined` for PNG (lossless — `HTMLCanvasElement.toBlob` ignores
   * the quality argument for non-lossy types anyway, but keeping this
   * explicit at call sites avoids ever passing a meaningless value). */
  quality: number | undefined
  /** Matches the real encoded bytes — callers must derive the uploaded
   * filename's extension AND the `File`'s `type` from the ACTUAL
   * resulting `Blob.type`, never re-hardcode `'image/png'`/`.png`
   * independently (that mismatch was a real latent bug this task also
   * fixed — see `CanvasImageEditorOverlay.tsx`'s `onSaveAsNew`). */
  extension: 'png' | 'jpg'
}

/** VistoeraTASK-00441 — high-quality (deliberately NOT a "default
 * low-quality JPEG", explicitly forbidden by this task's own spec) encode
 * for a confirmed-opaque result. 0.92 sits well above the ~0.75-0.85
 * range typical of lossy "web-safe" presets and is widely cited as
 * visually indistinguishable from the source for photographic content at
 * normal viewing sizes/zoom. */
export const OPAQUE_EXPORT_JPEG_QUALITY = 0.92

/** Chooses the edited-image export's final encoding. `hasTransparency`
 * must come from `imageDataHasTransparency` run against the FINAL
 * composited+cropped pixel buffer (post mask-cut), not the source image
 * or an intermediate layer. */
export function chooseEditedImageExportFormat(hasTransparency: boolean): EditedImageExportFormat {
  if (hasTransparency) return { mimeType: 'image/png', quality: undefined, extension: 'png' }
  return { mimeType: 'image/jpeg', quality: OPAQUE_EXPORT_JPEG_QUALITY, extension: 'jpg' }
}
